import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Navigation, List, Map as MapIcon, ArrowLeft, Globe } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import RecMap from "@/components/guest/RecMap";
import { InAppBrowser } from "@/components/guest/InAppBrowser";

const CATS = [
  { key: "restaurants", label: "Restaurants", img: "https://images.unsplash.com/photo-1515668236457-83c3b8764839?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", forceImg: "https://customer-assets-39nsmqrw.emergentagent.net/job_stay-compass-1/artifacts/7iisx07s_apache%20junction%20restraunts.png" },
  { key: "attractions", label: "Attractions", img: "https://images.unsplash.com/photo-1727072258729-2e23be04fb67?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", forceImg: "https://customer-assets-39nsmqrw.emergentagent.net/job_stay-compass-1/artifacts/5lozukx1_aj%20attractions%20logo.png" },
  { key: "hiking", label: "Hiking Trails", img: "https://images.unsplash.com/photo-1501554728187-ce583db33af7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", forceImg: "https://customer-assets-39nsmqrw.emergentagent.net/job_stay-compass-1/artifacts/tjf8mlqw_aj%20hiking.png" },
  { key: "grocery", label: "Grocery Stores", img: "https://images.unsplash.com/photo-1604145703889-5c58d94ee681?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", forceImg: "https://customer-assets-39nsmqrw.emergentagent.net/job_stay-compass-1/artifacts/ln52xmsv_aj%20grocery%20stores%20logo.png" },
  { key: "coffee", label: "Coffee Shops", img: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", forceImg: "/branding/aj-coffee-shops.png" },
  { key: "icecream", label: "Ice Cream Shops", img: "https://images.unsplash.com/photo-1501443762994-82bd5dace89a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", forceImg: "/branding/aj-ice-cream-shops.png" },
  { key: "golf", label: "Local Golf Courses", img: "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", forceImg: "/branding/local-golf-courses.png" },
  { key: "happenings", label: "Local Happenings", forceImg: "/branding/local-happenings.png", external: "https://visitaj.com/events/" },
];

export default function LocalTips() {
  const [items, setItems] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get("cat");
  const setCat = (key) => setSearchParams(key ? { cat: key } : {});
  const [view, setView] = useState("list");
  const [webView, setWebView] = useState(null);

  const isKiosk = typeof window !== "undefined" && (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true
  ) && Math.min(window.screen?.width || 9999, window.screen?.height || 9999) >= 600;
  const openExternal = (u, opts = {}) => {
    if (isKiosk) { setWebView({ url: u, ...opts }); return; }
    const w = window.open(u, "_blank");
    if (w) { w.opener = null; } else { setWebView({ url: u, ...opts }); }
  };

  useEffect(() => { api.get("/recommendations").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const filtered = cat ? items.filter((i) => i.category === cat) : [];
  const countFor = (k) => items.filter((i) => i.category === k).length;
  const imgFor = (c) => {
    if (c.forceImg) return c.forceImg;
    const first = items.find((i) => i.category === c.key && i.image_url);
    return (first && first.image_url) || c.img;
  };
  const activeCat = CATS.find((c) => c.key === cat);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <div className="mb-8">
        <span className="overline text-brand-terracotta">Explore</span>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">Local Spots</h1>
        {!cat && <p className="text-brand-forest/60 mt-2">Pick a category to see our recommendations.</p>}
      </div>

      {!cat ? (
        <div className="grid sm:grid-cols-2 gap-5 sm:gap-6" data-testid="category-grid">
          {CATS.map((c, i) => (
            <motion.button
              key={c.key}
              data-testid={`category-tile-${c.key}`}
              onClick={() => {
                if (c.external) {
                  openExternal(c.external, { title: c.label });
                } else {
                  setCat(c.key); setView("list");
                }
              }}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group text-left rounded-3xl overflow-hidden border border-black/5 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] active:scale-[0.98]"
            >
              <div className="relative aspect-[3/2] overflow-hidden bg-brand-sand">
                <img src={imgFor(c).startsWith("/") ? imgFor(c) : mediaUrl(imgFor(c))} alt={c.label} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            </motion.button>
          ))}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 mb-6">
            <button
              data-testid="back-to-categories"
              onClick={() => { setCat(null); setView("list"); }}
              className="flex items-center gap-1.5 text-sm font-semibold text-brand-forest/70 hover:text-brand-terracotta transition-colors active:scale-95"
            >
              <ArrowLeft size={18} /> All categories
            </button>
            <div className="flex rounded-full bg-white border border-black/5 p-1" data-testid="view-toggle">
              <button
                data-testid="view-list"
                onClick={() => setView("list")}
                className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors active:scale-95 ${view === "list" ? "bg-brand-forest text-white" : "text-brand-forest/60 hover:text-brand-terracotta"}`}
              >
                <List size={16} /> List
              </button>
              <button
                data-testid="view-map"
                onClick={() => setView("map")}
                className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors active:scale-95 ${view === "map" ? "bg-brand-forest text-white" : "text-brand-forest/60 hover:text-brand-terracotta"}`}
              >
                <MapIcon size={16} /> Map
              </button>
            </div>
          </div>

          <h2 className="font-display text-3xl tracking-tight mb-6" data-testid="active-category-title">{activeCat?.label}</h2>

          {view === "map" ? (
            <RecMap items={filtered} />
          ) : filtered.length === 0 ? (
            <p className="text-brand-forest/50 py-12 text-center">No recommendations here yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((item, i) => (
                <motion.div
                  key={item.id}
                  data-testid={`rec-card-${item.id}`}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white rounded-3xl overflow-hidden border border-black/5 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]"
                >
                  {item.image_url && (() => {
                    const isLogo = item.category === "golf" && !item.image_url.includes("images.unsplash.com");
                    return (
                      <div className={`h-44 overflow-hidden ${isLogo ? "flex items-center justify-center bg-white p-6" : ""}`}>
                        <img src={mediaUrl(item.image_url)} alt={item.title} className={isLogo ? "max-h-full max-w-full object-contain" : "h-full w-full object-cover"} />
                      </div>
                    );
                  })()}
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-display text-xl tracking-tight">{item.title}</h3>
                      {item.address && (
                        <button
                          type="button"
                        onClick={() => openExternal(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(item.address)}`, { title: `Directions to ${item.title}`, mode: "qr", distance: item.distance })}
                          data-testid={`rec-directions-${item.id}`}
                          className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-brand-terracotta text-brand-terracotta px-3 py-1.5 text-xs font-semibold hover:bg-brand-terracotta hover:text-white transition-colors active:scale-95"
                        >
                          <Navigation size={13} /> Directions
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-brand-forest/70 leading-relaxed mb-3">{item.description}</p>
                    {(item.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {item.tags.map((t) => (
                          <span key={t} className="text-xs text-brand-forest/60 bg-brand-bg rounded-full px-2.5 py-1">{t}</span>
                        ))}
                      </div>
                    )}
                    {item.address && (
                      <button
                        type="button"
                        onClick={() => openExternal(`https://maps.google.com/?q=${encodeURIComponent(item.address)}`, { title: `Directions to ${item.title}`, mode: "qr", distance: item.distance })}
                        data-testid={`rec-address-${item.id}`}
                        className="flex items-center gap-1.5 text-sm font-semibold text-brand-terracotta hover:underline text-left active:scale-95 transition-transform"
                      >
                        <Navigation size={15} /> {item.address}
                      </button>
                    )}
                    {item.website_url && (
                      <button
                        type="button"
                        onClick={() => openExternal(/^https?:\/\//i.test(item.website_url) ? item.website_url : `https://${item.website_url}`, { title: item.title })}
                        data-testid={`rec-website-${item.id}`}
                        className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-brand-terracotta hover:underline active:scale-95 transition-transform"
                      >
                        <Globe size={15} /> Visit Website
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}
      <InAppBrowser url={webView?.url} title={webView?.title} mode={webView?.mode} distance={webView?.distance} onClose={() => setWebView(null)} />
    </div>
  );
}
